/**
 * Visit — address, hours, and a map.
 *
 * ─────────────────────────────────────────────────────────────
 * THE MAP
 * ─────────────────────────────────────────────────────────────
 * Prefers the Google Maps Embed API when VITE_GOOGLE_MAPS_KEY is set. That
 * is the licensed path: the Embed API is free and explicitly permits this
 * use, and it stays current when the shop's surroundings change.
 *
 * Without a key it falls back to a still. The still is a satellite capture
 * from the design handoff, cropped to drop the app furniture. It renders
 * correctly, but it is a screenshot of a mapping product, and redistributing
 * one on a commercial site is a licensing question rather than a settled
 * right — so it is explicitly an interim, and the console says so once in
 * development. Setting the key retires it.
 *
 * Hours come from shop.config.json and are formatted for reading, not for
 * storage: "9am – 6pm", never "09:00 – 18:00".
 *
 * ─────────────────────────────────────────────────────────────
 * MERGED WITH WALK-INS
 * ─────────────────────────────────────────────────────────────
 * Walk-ins and Visit were two sections saying the same thing: come by.
 * They are one section now, led by the crest panel — the logo IS the
 * header, so there is no eyebrow above it. #walkins survives as an anchor
 * at the top so older links and printed material still land.
 *
 * The Call us button is hidden below lg, exactly where MobileStickyBar
 * (lg:hidden) already pins a Call button to the bottom of the screen.
 * The two breakpoints are complements on purpose: one Call control is
 * reachable at every width, never two.
 */

import { useEffect } from 'react';
import { useCopy, useLocale } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { timeRange } from '../../lib/formatTime';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;

export default function VisitSection() {
  const c = useCopy().home.visit;
  const walkins = useCopy().home.walkins;
  const { t, shop, hours } = useShopConfig();
  const locale = useLocale();

  useEffect(() => {
    if (MAPS_KEY || !import.meta.env.DEV) return;
    console.warn(
      'VisitSection: no VITE_GOOGLE_MAPS_KEY — rendering a placeholder tile ' +
        'instead of the map. Set it in .env.local locally, and as a GitHub ' +
        'Actions secret for deploys (Vite inlines VITE_* at build time).'
    );
  }, []);

  const fullAddress = `${shop.address_line_1}, ${shop.city}, ${shop.state} ${shop.zip}`;
  const encoded = encodeURIComponent(fullAddress);

  const row = (day: string) => hours.find((h) => h.day === day);
  const weekday = row('Mon');
  const saturday = row('Sat');

  const rows = [
    {
      label: t(c.hoursCard.weekdays),
      value: weekday?.open && weekday.close ? timeRange(weekday.open, weekday.close, locale) : '',
      closed: false,
    },
    {
      label: t(c.hoursCard.saturday),
      value:
        saturday?.open && saturday.close ? timeRange(saturday.open, saturday.close, locale) : '',
      closed: false,
    },
    { label: t(c.hoursCard.sunday), value: t(c.hoursCard.sundayClosed), closed: true },
  ];

  return (
    <section id="visit" className="bg-paper px-6 py-16 md:px-12 md:py-24">
      {/* Old deep links and printed cards still point at #walkins. */}
      <span id="walkins" aria-hidden className="block" />

      <div className="mx-auto max-w-6xl">
        {/* The crest panel is the section header — no tag above it. */}
        <div
          className="relative flex min-h-[320px] items-center justify-center overflow-hidden rounded-card bg-edge-1 bg-cover bg-center shadow-[0_24px_60px_rgba(11,27,58,0.14)] md:min-h-[420px]"
          style={{ backgroundImage: "url('/assets/textures/marble.jpg')" }}
        >
          <span className="pointer-events-none absolute inset-[26px] rounded-2xl border-2 border-gold" />
          <img
            src="/assets/logo/kd-crest.png"
            alt=""
            role="presentation"
            width={1024}
            height={1536}
            loading="lazy"
            className="relative w-[46%] max-w-[320px] object-contain drop-shadow-[0_20px_40px_rgba(11,27,58,0.3)]"
          />
        </div>

        <h2 className="mt-12 text-balance font-newsreader text-[40px] font-medium leading-[1.05] text-navy md:mt-16 md:text-[56px]">
          {t(walkins.headline)}
        </h2>
      </div>

      <div className="mx-auto mt-12 grid max-w-6xl gap-10 md:mt-16 md:grid-cols-2 md:items-start md:gap-16">
        <div className="flex flex-col gap-6">
          <h3 className="text-balance font-newsreader text-[28px] font-medium leading-[1.15] text-navy md:text-[36px]">
            {t(c.headline)}
          </h3>
          <div className="mt-2 flex flex-col divide-y divide-edge-1 rounded-card border border-edge-1 bg-white px-6 py-2 md:mt-4 md:px-8">
            {rows.map((r) => (
              <div
                key={r.label}
                className="flex items-center justify-between gap-4 py-4 font-newsreader text-[17px] md:text-[18px]"
              >
                <span className="text-navy">{r.label}</span>
                <span
                  className={
                    r.closed ? 'text-right italic text-warm-muted' : 'text-right text-gold'
                  }
                >
                  {r.value}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap gap-3">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encoded}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-pill border border-edge-2 px-8 py-4 font-newsreader text-[19px] text-navy transition-colors hover:border-navy hover:bg-white"
            >
              {t(c.ctaSecondary)}
            </a>
            {/* hidden below lg — MobileStickyBar (lg:hidden) already pins a
                Call button there, and two would compete. */}
            <a
              href={`tel:${shop.phone_e164}`}
              className="hidden rounded-pill bg-navy px-8 py-4 font-newsreader text-[19px] text-white transition-colors hover:bg-red-accent lg:inline-block"
            >
              {t(c.callUs)}
            </a>
          </div>
        </div>

        <div className="relative h-[380px] overflow-hidden rounded-card shadow-[0_24px_60px_rgba(11,27,58,0.14)] md:h-[540px]">
          {MAPS_KEY ? (
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${encoded}&zoom=17&maptype=satellite`}
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={t(c.mapTitle)}
            />
          ) : (
            <div
              role="img"
              aria-label={t(c.mapAlt)}
              className="flex h-full w-full items-center justify-center bg-edge-1 bg-cover bg-center"
              style={{ backgroundImage: "url('/assets/textures/marble.jpg')" }}
            >
              {import.meta.env.DEV && (
                <span className="rounded-pill bg-navy/90 px-4 py-2 font-oswald text-[11px] uppercase tracking-[0.18em] text-white">
                  VITE_GOOGLE_MAPS_KEY not set
                </span>
              )}
            </div>
          )}

          {/* bottom-14, not bottom-6: the Google wordmark and the "Map data ©"
              strip live in the embed's bottom-left corner, and the Maps
              Platform terms require that attribution stay unobscured. */}
          <div className="absolute bottom-14 left-6 flex items-center gap-3 rounded-pill bg-white py-2 pl-2 pr-6 shadow-[0_10px_30px_rgba(11,27,58,0.14)]">
            <img
              src="/assets/logo/kd-crest-square.png"
              alt=""
              role="presentation"
              className="h-10 w-10 rounded-full object-cover"
            />
            <span className="flex flex-col">
              <span className="font-newsreader text-[15px] font-medium leading-tight text-navy">
                {t(c.pinTitle)}
              </span>
              <span className="mt-0.5 font-newsreader text-[12px] leading-tight text-warm-muted">
                {t(c.pinSubtitle)}
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
